import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { handleLogout } from "../auth/authHelpers";
import {
  cachedMe,
  fetchInvite,
  homePathFor,
  landingPathAfterLogin,
  orgErrorMessage,
  readSession,
} from "../../utils/organisationApi";

const readSignedInEmail = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("user_data") || "null");
    if (stored?.email) return stored.email;
  } catch {
    // Fall through to the cached profile
  }
  return cachedMe()?.user?.email || "";
};

const sameEmail = (left, right) => left.trim().toLowerCase() === right.trim().toLowerCase();

const outlineButtonClass =
  "mt-6 inline-flex justify-center w-full py-2.5 px-4 rounded-md text-accent-blue font-semibold border border-[#c5d4ef] hover:bg-[#f4f7fc]";

// /invite?token=... from the invite email. The link expires after 7 days.
// A usable invite goes to login with the invited email filled in; the token rides along in next.
const AcceptInvitePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get("token") || "";
  const isLoggedIn = Boolean(localStorage.getItem("jwtToken"));
  const signedInEmail = readSignedInEmail().trim();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoadError("This invite link isn't valid.");
      setLoading(false);
      return;
    }
    fetchInvite(token)
      .then(setInvite)
      .catch((err) => setLoadError(orgErrorMessage(err, "This invite link isn't valid.")))
      .finally(() => setLoading(false));
  }, [token]);

  const loginHref = `/login?email=${encodeURIComponent(invite?.email || "")}&next=${encodeURIComponent(`/invite?token=${token}`)}`;

  const currentHome = isLoggedIn ? homePathFor(readSession()) : "/login";
  const homeLabel = isLoggedIn ? "Go to Sift Hire" : "Back to sign in";

  const shell = (children) => (
    <div className="min-h-screen bg-[#f4f7fb] flex flex-col items-center justify-center px-4 py-8 sm:px-6">
      <img src="/sift-hire-logo.png" alt="SiftHire" className="w-40 mb-5" />
      <div className="w-full max-w-2xl min-w-0">
        <div className="bg-white rounded-xl border border-[#d5deec] shadow-[0_10px_30px_rgba(34,82,165,0.08)] px-5 py-7 sm:px-8 sm:py-8">
          {children}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return shell(<p className="text-center text-accent-gray-medium">Checking your invite...</p>);
  }

  if (loadError) {
    return shell(
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-black">Invite unavailable</h1>
        <p className="text-accent-red mt-3">{loadError}</p>
        <p className="text-sm text-accent-gray-medium mt-3">Ask the person who invited you to send a new one.</p>
        <Link to={currentHome} className={outlineButtonClass}>
          {homeLabel}
        </Link>
      </div>
    );
  }

  if (invite.status === "expired") {
    const organisation = invite.organisationName || "your organisation";
    return shell(
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-black">Invite expired</h1>
        <p className="text-accent-gray-medium mt-3">Ask {organisation} to resend it.</p>
        <Link to={currentHome} className={outlineButtonClass}>
          {homeLabel}
        </Link>
      </div>
    );
  }

  const invitedEmail = (invite.email || "").trim();
  if (isLoggedIn && invitedEmail && signedInEmail && !sameEmail(signedInEmail, invitedEmail)) {
    return shell(
      <div className="text-center max-w-md mx-auto">
        <h1 className="text-xl font-semibold text-black">You're logged in with a different account</h1>
        <p className="text-sm text-accent-gray-medium mt-3">
          This invitation was sent to a different email address.
        </p>
        <div className="mt-6 flex flex-col gap-4 text-left">
          <div className="rounded-md border border-[#d5deec] bg-[#f4f7fb] px-4 py-3">
            <p className="text-xs text-accent-gray-medium">Currently logged in as</p>
            <p className="mt-1 text-sm font-semibold text-black">{signedInEmail}</p>
          </div>
          <div className="rounded-md border border-[#d5deec] bg-[#f4f7fb] px-4 py-3">
            <p className="text-xs text-accent-gray-medium">Invitation sent to</p>
            <p className="mt-1 text-sm font-semibold text-black">{invitedEmail}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => handleLogout(`/invite?token=${encodeURIComponent(token)}`)}
          className="mt-6 w-full py-2.5 px-4 text-base font-semibold rounded-md text-white bg-accent-blue hover:bg-accent-blue-hover"
        >
          Sign out
        </button>
        <button
          type="button"
          onClick={() => navigate(landingPathAfterLogin(), { replace: true })}
          className="mt-3 w-full py-2.5 px-4 text-base font-semibold rounded-md text-accent-blue border border-[#c5d4ef] hover:bg-[#f4f7fc]"
        >
          Continue with this account
        </button>
      </div>
    );
  }

  // The login form sends the token with the code, which creates the account if needed and joins
  return <Navigate to={loginHref} replace />;
};

export default AcceptInvitePage;
